import { create } from 'zustand';
import type { KioskConfig } from '../lib/api';

export type PaymentMethod = 'CASH' | 'QRIS' | 'EDC';

interface UIState {
  isLoading: boolean;
  error: string | null;
  inactivityWarningVisible: boolean;
  inactivityCountdown: number;
  /** Path passed into /error for recovery routing (e.g. '/walk-up') */
  errorReturnScreen: string;
  /** Selected payment method for top-up / purchase flows */
  selectedPaymentMethod: PaymentMethod | null;
  /** Kiosk device config fetched on app init (enabled payment methods, EDC banks) */
  kioskConfig: KioskConfig | null;
  /** EDC bank selected by operator on the payment screen */
  selectedEdcBank: string | null;

  // Actions
  setLoading: (loading: boolean) => void;
  setError: (error: string | null, returnTo?: string) => void;
  clearError: () => void;
  showInactivityWarning: (countdown: number) => void;
  hideInactivityWarning: () => void;
  setInactivityCountdown: (seconds: number) => void;
  setPaymentMethod: (method: PaymentMethod | null) => void;
  setKioskConfig: (config: KioskConfig) => void;
  setEdcBank: (bank: string | null) => void;
}

export const useUIStore = create<UIState>((set) => ({
  isLoading: false,
  error: null,
  inactivityWarningVisible: false,
  inactivityCountdown: 10,
  errorReturnScreen: '/',
  selectedPaymentMethod: null,
  kioskConfig: null,
  selectedEdcBank: null,

  setLoading: (loading) => set({ isLoading: loading }),

  setError: (error, returnTo = '/') =>
    set({
      error,
      isLoading: false,
      errorReturnScreen: returnTo,
    }),

  clearError: () => set({ error: null }),

  showInactivityWarning: (countdown) =>
    set({ inactivityWarningVisible: true, inactivityCountdown: countdown }),

  hideInactivityWarning: () =>
    set({ inactivityWarningVisible: false, inactivityCountdown: 10 }),

  setInactivityCountdown: (seconds) => set({ inactivityCountdown: seconds }),

  setPaymentMethod: (method) => set({ selectedPaymentMethod: method }),
  setKioskConfig: (config) => set({ kioskConfig: config }),
  setEdcBank: (bank) => set({ selectedEdcBank: bank }),
}));
